import { PropertyValueSet } from "@genesys/property";
import { ActiveLoadState, LoadDef } from "./types";
import { getDefaultCaseOverrides } from "../../../../tools";
import * as OperationTimeGen2 from "../../../../../operation-time-manager";
import * as SettingsOverride from "../../../../components/settings-overrides";
import { OperatingCaseGroupEnum } from "@genesys/graphql-types";

export function createLoadState(
  loadId: string,
  sortNo: number,
  loadDef: LoadDef,
  name: string,
  loadSettings: PropertyValueSet.PropertyValueSet,
  operationTime?: OperationTimeGen2.OperationTime,
  caseOverrides?: SettingsOverride.SettingsOverrides
): ActiveLoadState {
  const operationTimeState = initializeOperationTimeState(
    loadSettings,
    loadDef,
    operationTime
  );

  const isOverriden = loadDef.properties.manualInputKey
    ? PropertyValueSet.getInteger(
        loadDef.properties.manualInputKey,
        loadSettings
      ) === 1
    : false;

  const initialCaseOverrides = initializeCaseOverrides(
    isOverriden,
    loadSettings,
    loadDef.properties.overridableProperties,
    caseOverrides
  );

  // Filter out properties that are not in the load definition
  const filteredSettings = PropertyValueSet.keepProperties(
    loadDef.properties.loadProperties,
    loadSettings
  );

  return {
    id: loadId,
    settings: filteredSettings,
    sortNo,
    name,
    caseOverrides: initialCaseOverrides,
    operationTimeState
  };
}

function initializeOperationTimeState(
  loadSettings: PropertyValueSet.PropertyValueSet,
  loadDef: LoadDef,
  currentOperationTime?: OperationTimeGen2.OperationTime
): OperationTimeGen2.State {
  const initialOperationalTimes =
    currentOperationTime ||
    determineInitialOperationalTimes(loadSettings, loadDef);
  return OperationTimeGen2.init(initialOperationalTimes)[0];
}

function initializeCaseOverrides(
  isOverriden: boolean,
  loadSettings: PropertyValueSet.PropertyValueSet,
  loadOveridableProperties: ReadonlyArray<string>,
  caseOverrides?: SettingsOverride.SettingsOverrides
): SettingsOverride.SettingsOverrides {
  if (isOverriden) {
    return (
      caseOverrides ||
      filterCaseOverrideGroupSettings(
        getDefaultCaseOverrides(loadSettings),
        loadOveridableProperties
      )
    );
  }

  return getDefaultCaseOverrides(PropertyValueSet.Empty);
}

function filterCaseOverrideGroupSettings(
  caseOverrides: SettingsOverride.SettingsOverrides,
  overidableProperties: ReadonlyArray<string>
): SettingsOverride.SettingsOverrides {
  return Object.keys(caseOverrides).reduce((acc, key) => {
    return {
      ...acc,
      [key as OperatingCaseGroupEnum]: PropertyValueSet.keepProperties(
        overidableProperties,
        caseOverrides[key as OperatingCaseGroupEnum]
      )
    };
  }, {} as SettingsOverride.SettingsOverrides);
}

function determineInitialOperationalTimes(
  loadSettings: PropertyValueSet.PropertyValueSet,
  loadDef: LoadDef
): OperationTimeGen2.State {
  return loadDef.operationalTime.getDefaultOperationTime
    ? loadDef.operationalTime.getDefaultOperationTime(loadSettings)
    : OperationTimeGen2.presets.factoryHours;
}
